import {
  ONTARIO_PRE_NEED_DEFAULT_LINES,
  PRE_NEED_DEFAULT_LINES,
  cloneDefaultLines,
  linesMatchDefaults,
} from 'paperman-contract-defaults';

/**
 * Pre-plan only (T-172): a pre-need the family wants kept on file with no
 * contract to sign and nothing to pay. Stored on `funerals.preneed_mode`;
 * 'prepaid' is the default and every case before T-172.
 *
 * Mirrors `isPreplanOnly` in nestjs case-state/process-definition.ts.
 */
export type PreneedMode = 'prepaid' | 'preplan';

type ModeFields = {
  preneed_mode?: string | null;
  has_atneed?: boolean | null;
  contracts_signed_at?: string | null;
};

/**
 * True while the case is a pre-plan only. Ends on its own once the case is
 * at-need (the person died and the pre-plan was converted; the at-need
 * contract is then sent, signed and paid like any other), or once a contract
 * is signed anyway (a signed pre-need is prepaid).
 */
export function isPreplanOnly(f: ModeFields | null | undefined): boolean {
  return f?.preneed_mode === 'preplan' && f?.has_atneed !== true && !f?.contracts_signed_at;
}

/**
 * Why the switch can't be flipped, or null when it can.
 *
 * A signed pre-need is prepaid: TruStage sets up the payments from the signed
 * contract, so calling it "pre-plan only" would be false. A sent-but-unsigned
 * contract doesn't lock it; turning the switch on just stops the chasing.
 */
export function preplanLockReason(
  f: { contracts_signed_at?: string | null } | null | undefined,
): string | null {
  if (f?.contracts_signed_at) {
    return 'Locked: the pre-need contract is signed, so this plan is prepaid.';
  }
  return null;
}

type ContractLine = { item: string | number; quantity?: number };

/**
 * The contract update that turns a pre-plan's pre-need contract into an
 * at-need one, when the person has died.
 *
 * This is NOT the TruStage "Fruition" (fulfill-preneed): there is no policy
 * to fulfil and nothing was paid, so it books no partner payment and marks
 * nothing paid. The contract becomes an ordinary at-need contract that the
 * family signs and pays.
 *
 * Lines that are still the pre-need defaults are swapped for the at-need
 * defaults, same rule as the Contract Type toggle on the contract form. Edited
 * lines are kept for staff to review. Prices and totals are recomputed by the
 * QuickBooks estimate sync afterwards.
 */
export function preplanToAtNeedPatch(
  contract: { lines?: ContractLine[] | null },
  region?: string | null,
): {
  is_preneed: false;
  is_monthly: false;
  monthly_payment_amount: null;
  lines?: ReturnType<typeof cloneDefaultLines>;
} {
  const lines = contract.lines ?? [];
  const isOntario = region?.toLowerCase() === 'ontario';
  const preDefaults = isOntario ? ONTARIO_PRE_NEED_DEFAULT_LINES : PRE_NEED_DEFAULT_LINES;
  const swapLines = lines.length === 0 || linesMatchDefaults(lines, preDefaults);
  return {
    is_preneed: false,
    is_monthly: false,
    monthly_payment_amount: null,
    ...(swapLines ? { lines: cloneDefaultLines('at', region ?? undefined) } : {}),
  };
}
